/**
 * Custom element powering the customer-reviews masonry wall:
 *
 * - Numbered pagination, flexible count per page WITHIN a fixed range -
 *   no weight/KB computation (an earlier version of this file did that;
 *   retracted, see review-wall.liquid's doc comment), and no longer a
 *   single strict number either (also retracted). Desktop/tablet: 12-15
 *   reviews/page (raised from 10-13 - the visible gap between the last
 *   partial row of cards and the pagination strip below was too large).
 *   Mobile: 6-8 reviews/page, unchanged. The exact count per page is
 *   chosen purely for a visually even split - see #computePageSizes() -
 *   explicitly NOT a performance/weight decision (per the merchant: "pas
 *   une histoire de performance"). Clicking a page number REPLACES the
 *   visible cards with that page's cards (previous page hidden, nothing
 *   accumulates) - not a "load more" that stacks. Same range-based model
 *   for every page, including page 1 - no special case for it.
 *
 * - #computePageSizes(): given a pool size, picks the SMALLEST page count
 *   that keeps every page at or under the range's max, then spreads the
 *   total as evenly as possible across those pages (some pages get one
 *   more than others, never a lopsided leftover page) - e.g. 62 reviews on
 *   desktop (max 15) becomes 5 pages of [13,13,12,12,12], not 4×13 then a
 *   sparse page of 10. This is a count-based even split, not a true
 *   rendered-pixel-height balance (that would need an actual browser layout
 *   pass to measure, not something a script can simulate reliably) - a
 *   reasonable, honest proxy for "avoid an empty or overloaded page" given
 *   the constraints, not a claim of pixel-perfect masonry balance.
 *
 * - The page-number strip itself is generated here, not in Liquid, because
 *   the real page count depends on the pool size (default vs filtered) and
 *   the viewport, both only known client-side. Deliberately minimal, not a
 *   search-engine-style "1 2 … 27" strip (retracted - see #renderPaginationNav):
 *   only the current page and up to 2 pages ahead of it are ever shown, no
 *   "…", the real total page count is never displayed at all - finding a
 *   specific review is expected to go through the rating filter, not deep
 *   pagination. prev/next arrows flank the numbers either way.
 *
 * - Default pool (no rating filter) is ALL 381 reviews, in the fixed
 *   curated order: the 62 media reviews first (pages 1-5 currently, exactly
 *   as before this changed), then the 319 text-only reviews continuing on
 *   the pages after (see #recomputePages() - media and text are paginated
 *   as two separate runs then concatenated, so that boundary always falls
 *   exactly between a media page and a text page, never split across one).
 *   Retracted the earlier "default pool is media-only, text hidden behind
 *   the filter" rule - the merchant now wants text reviews reachable by
 *   paging through normally, not gated behind filtering.
 *
 * - Rating filter: a "Filtre" button opens a small dropdown (5/4/3/2/1
 *   étoiles + "toutes les notes" to clear). Selecting a rating (a) resets
 *   to page 1 and (b) switches the pool to ALL 381 reviews (media +
 *   text-only) matching that exact rating, paginated the same range-based
 *   way, computed as a single run (no media/text boundary to preserve for
 *   a filtered result) - works in parallel with the default pool above,
 *   unaffected by this change.
 *
 * - Click-to-open lightbox (media + full text + name + stars). Reads
 *   author/text from the clicked card's own visible
 *   `.review-card__author`/`.review-card__text` nodes - no separate
 *   data-attribute copy of that text (removed, it just duplicated bytes
 *   already in the visible markup). Same pattern for the optional brand
 *   reply (`.review-card__reply-text`) - most reviews don't have one, see
 *   _review-item.liquid's `brand_reply` setting. That node is now ALSO
 *   visible directly in the card itself (a clamped preview, so a reply's
 *   existence is obvious without opening the lightbox) as well as
 *   feeding the lightbox's own `.review-lightbox__reply` box when
 *   non-empty - `.textContent` always returns the full untruncated text
 *   regardless of the card's own line-clamp styling, so this lightbox
 *   logic needed no change when that preview was added.
 * - Lightbox multi-image navigation for reviews with several photos.
 * - Closes on backdrop click or the explicit close button, Escape key too.
 *
 * All cards are rendered server-side from the start; pagination/filtering
 * only toggles `hidden`, it never fetches anything. Video cards are the one
 * exception to "everything's already there": the grid only ever renders a
 * placeholder box (`.review-card__media--video`, see _review-item.liquid)
 * carrying the real URL in a data attribute - no `<video>` tag, and so no
 * preload/metadata network request, exists until this lightbox actually
 * creates one on click for the specific review being opened. Retracted the
 * previous approach (a real `<video preload="metadata">` per video card,
 * used as a free first-frame thumbnail) once it was confirmed that `hidden`/
 * `display:none` does not stop a browser from fetching video metadata -
 * unlike `<img loading="lazy">`, `<video>` has no native viewport-aware
 * deferral, so with 27 video reviews that meant up to 27 metadata requests
 * on every page load regardless of pagination.
 */
class ReviewWall extends HTMLElement {
  // Reviews-per-page range, purely for visual balance (see #computePageSizes
  // and the class doc comment) - not weight/performance-driven.
  static PAGE_SIZE_DESKTOP_MIN = 12;
  static PAGE_SIZE_DESKTOP_MAX = 15;
  static PAGE_SIZE_MOBILE_MIN = 6;
  static PAGE_SIZE_MOBILE_MAX = 8;

  connectedCallback() {
    this.grid = this.querySelector('[data-review-grid]');
    this.paginationNav = this.querySelector('[data-review-pagination]');
    this.allCards = Array.from(this.querySelectorAll('[data-review-card]'));
    this.mediaCards = this.allCards.filter((card) => card.dataset.mediaType !== 'none');

    this.isDesktop = window.matchMedia('(min-width: 750px)').matches;

    this.ratingFilter = null; // null = toutes les notes, sinon 1-5
    this.pages = []; // cumulative card-count boundaries for the active list
    this.currentPage = 1;

    this.filterToggle = this.querySelector('[data-review-filter-toggle]');
    this.ratingDropdown = this.querySelector('[data-review-rating-dropdown]');
    this.ratingOptions = Array.from(this.querySelectorAll('[data-review-rating-option]'));
    this.emptyState = this.querySelector('[data-review-empty-state]');

    this.lightbox = this.querySelector('[data-review-lightbox]');
    this.lightboxMedia = this.querySelector('[data-review-lightbox-media]');
    this.lightboxName = this.querySelector('[data-review-lightbox-name]');
    this.lightboxStars = this.querySelector('[data-review-lightbox-stars]');
    this.lightboxText = this.querySelector('[data-review-lightbox-text]');
    this.lightboxReply = this.querySelector('[data-review-lightbox-reply]');
    this.lightboxReplyText = this.querySelector('[data-review-lightbox-reply-text]');
    this.lightboxPrev = this.querySelector('[data-review-lightbox-prev]');
    this.lightboxNext = this.querySelector('[data-review-lightbox-next]');
    this.activeCard = null;
    this.activeImageIndex = 0;

    // Hands control over to JS (see the matching CSS fallback rule in
    // review-wall.liquid) - must happen before #applyVisibility() so there's
    // no frame where both the CSS fallback and the JS's own hidden-attribute
    // logic are fighting over the same cards.
    this.classList.add('review-wall--js-ready');

    this.#recomputePages();
    this.#applyVisibility();

    this.filterToggle?.addEventListener('click', (event) => {
      event.stopPropagation();
      const expanded = this.filterToggle.getAttribute('aria-expanded') === 'true';
      this.#setDropdownOpen(!expanded);
    });

    this.ratingOptions.forEach((option) => {
      option.addEventListener('click', () => {
        const raw = option.dataset.reviewRatingOption;
        this.ratingFilter = raw === '' ? null : Number(raw);
        this.ratingOptions.forEach((o) => o.setAttribute('aria-checked', String(o === option)));
        this.filterToggle?.classList.toggle('review-wall__filter-toggle--active', this.ratingFilter !== null);
        this.currentPage = 1;
        this.#recomputePages();
        this.#setDropdownOpen(false);
        this.#applyVisibility();
      });
    });

    document.addEventListener('click', (event) => {
      if (!this.ratingDropdown || this.ratingDropdown.hidden) return;
      if (!this.contains(event.target)) this.#setDropdownOpen(false);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.#setDropdownOpen(false);
    });

    this.allCards.forEach((card) => {
      card.addEventListener('click', () => this.#openLightbox(card));
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.#openLightbox(card);
        }
      });
    });

    this.lightbox?.addEventListener('click', (event) => {
      // Native <dialog> click coordinates: a click that lands on the dialog
      // element itself (not its content) means the backdrop was clicked.
      if (event.target === this.lightbox) this.#closeLightbox();
    });

    this.querySelector('[data-review-lightbox-close]')?.addEventListener('click', () => this.#closeLightbox());
    this.lightboxPrev?.addEventListener('click', () => this.#stepImage(-1));
    this.lightboxNext?.addEventListener('click', () => this.#stepImage(1));

    this.lightbox?.addEventListener('cancel', (event) => {
      // Handles the Escape key on <dialog> - prevent default to keep our own
      // close routine (cleans up aria/focus consistently either way).
      event.preventDefault();
      this.#closeLightbox();
    });
  }

  #setDropdownOpen(open) {
    if (!this.ratingDropdown || !this.filterToggle) return;
    this.ratingDropdown.hidden = !open;
    this.filterToggle.setAttribute('aria-expanded', String(open));
  }

  #activeList() {
    // Pas de filtre -> les 381, dans l'ordre fixe déjà en place (les 62
    // avis à média d'abord, puis les 319 avis texte - voir #recomputePages
    // pour comment la pagination respecte quand même une frontière nette
    // entre les deux plutôt que de les mélanger). Filtre actif -> les 381,
    // restreints à cette note (média et texte confondus, comme avant).
    if (this.ratingFilter === null) return this.allCards;
    return this.allCards.filter((card) => Number(card.dataset.reviewRating) === this.ratingFilter);
  }

  /**
   * Page sizes for a pool of `total` cards, within the current device's
   * [min, max] range, distributed as evenly as possible (see the class
   * doc comment for the reasoning). Picks the fewest pages that keep every
   * page at or under `max`, then spreads the remainder across the first
   * few pages one at a time - so a page is 14 or 15, never a 15-15-15-15-10
   * shape with a visually sparse last page.
   *
   * `min` isn't used in the formula itself: starting from the fewest pages
   * satisfying `max` already maximizes the average page size for that
   * total, so every real pool size in this dataset (verified: the default
   * 62-review pool and all three populated rating filters) naturally lands
   * at or above `min` too. It would only be possible to dip under `min` in
   * a total/range combination where no split can satisfy both bounds at
   * once (e.g. 18 reviews with a 12-15 range) - a purely theoretical case
   * for the data as it exists today; if it ever occurs, this favors never
   * exceeding `max` over never dipping under `min`.
   */
  #computePageSizes(total) {
    if (total === 0) return [];
    const max = this.isDesktop ? ReviewWall.PAGE_SIZE_DESKTOP_MAX : ReviewWall.PAGE_SIZE_MOBILE_MAX;

    const pageCount = Math.max(1, Math.ceil(total / max));
    const base = Math.floor(total / pageCount);
    const remainder = total % pageCount;
    return Array.from({ length: pageCount }, (_, i) => (i < remainder ? base + 1 : base));
  }

  /**
   * Pool par défaut (pas de filtre) : média et texte paginés SÉPARÉMENT
   * (chacun avec #computePageSizes) puis mis bout à bout, plutôt qu'un
   * seul calcul sur les 381 d'un coup - deux raisons : (1) garde les pages
   * média BYTE-FOR-BYTE identiques à avant ce changement (un calcul unique
   * sur 381 aurait légèrement redistribué leurs tailles) ; (2) garantit une
   * frontière nette entre les deux - jamais une page avec un mélange
   * média+texte. #activeList() renvoie les 381 dans cet ordre (média
   * d'abord), donc ces limites de page s'alignent correctement dessus.
   * Pool filtré par note : calcul unique sur le résultat filtré, comme
   * avant - un filtre n'a pas cette contrainte de frontière nette.
   */
  #recomputePages() {
    let sizes;
    if (this.ratingFilter === null) {
      const mediaSizes = this.#computePageSizes(this.mediaCards.length);
      const textCount = this.allCards.length - this.mediaCards.length;
      const textSizes = this.#computePageSizes(textCount);
      sizes = mediaSizes.concat(textSizes);
    } else {
      sizes = this.#computePageSizes(this.#activeList().length);
    }

    const boundaries = [];
    let cumulative = 0;
    sizes.forEach((size) => {
      cumulative += size;
      boundaries.push(cumulative);
    });
    this.pages = boundaries;
  }

  #applyVisibility() {
    const active = this.#activeList();
    const activeSet = new Set(active);
    const pageIndex = this.currentPage - 1;
    const start = pageIndex > 0 ? this.pages[pageIndex - 1] : 0;
    const end = this.pages[pageIndex] || 0;
    const visibleSet = new Set(active.slice(start, end));

    this.allCards.forEach((card) => {
      card.hidden = activeSet.has(card) ? !visibleSet.has(card) : true;
    });

    // Un filtre par note peut légitimement ne matcher aucun avis (ex. 1-2
    // étoiles, aucun avis à ces notes actuellement) - sans ce message, la
    // grille devient silencieusement vide, ce qui se lit comme "le filtre
    // ne fait rien" plutôt que "0 résultat pour cette note". N'apparaît que
    // filtre actif + 0 résultat - jamais sur la vue par défaut (sans filtre,
    // active.length est toujours > 0 puisque les 381 avis existent tous).
    if (this.emptyState) {
      this.emptyState.hidden = !(this.ratingFilter !== null && active.length === 0);
    }

    if (this.grid) {
      this.grid.hidden = this.ratingFilter !== null && active.length === 0;
    }

    this.#renderPaginationNav();
  }

  #goToPage(page) {
    const clamped = Math.max(1, Math.min(page, this.pages.length || 1));
    if (clamped === this.currentPage) return;
    this.currentPage = clamped;
    this.#applyVisibility();
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  #renderPaginationNav() {
    if (!this.paginationNav) return;
    const totalPages = this.pages.length;

    if (totalPages <= 1) {
      this.paginationNav.hidden = true;
      this.paginationNav.innerHTML = '';
      return;
    }

    this.paginationNav.hidden = false;
    const current = this.currentPage;

    // Volontairement simplifie (retire l'ancien "1 2 … 27" façon moteur de
    // recherche) : ni "…", ni le nombre total de pages jamais affiché - la
    // page courante et les 2 suivantes seulement, jamais plus. La fenêtre
    // avance avec la page courante (clic sur "3" ou sur la flèche suivante
    // depuis 3 affiche "3 4 5", etc.) - la recherche d'un avis précis passe
    // par le filtre par étoiles, pas par la pagination profonde.
    const numbers = [current, current + 1, current + 2].filter((p) => p >= 1 && p <= totalPages);

    const items = [];
    items.push(this.#arrowItemHtml('prev', current > 1));

    numbers.forEach((page) => {
      const isCurrent = page === current;
      items.push(
        `<li><button type="button" class="review-wall__page-btn${isCurrent ? ' review-wall__page-btn--current' : ''}" data-review-page="${page}"${isCurrent ? ' aria-current="page"' : ''}>${page}</button></li>`
      );
    });

    items.push(this.#arrowItemHtml('next', current < totalPages));

    this.paginationNav.innerHTML = `<ul class="review-wall__pagination-list">${items.join('')}</ul>`;

    this.paginationNav.querySelectorAll('[data-review-page]').forEach((btn) => {
      btn.addEventListener('click', () => this.#goToPage(Number(btn.dataset.reviewPage)));
    });
    this.paginationNav.querySelectorAll('[data-review-page-arrow]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const direction = btn.dataset.reviewPageArrow === 'next' ? 1 : -1;
        this.#goToPage(this.currentPage + direction);
      });
    });
  }

  #arrowItemHtml(direction, enabled) {
    const label = direction === 'prev' ? 'Page précédente' : 'Page suivante';
    const symbol = direction === 'prev' ? '‹' : '›';
    return `<li><button type="button" class="review-wall__page-arrow" data-review-page-arrow="${direction}" aria-label="${label}"${enabled ? '' : ' disabled'}>${symbol}</button></li>`;
  }

  #openLightbox(card) {
    if (!this.lightbox) return;
    this.activeCard = card;
    this.activeImageIndex = 0;
    this.#renderLightboxContent();
    if (typeof this.lightbox.showModal === 'function') {
      this.lightbox.showModal();
    } else {
      this.lightbox.setAttribute('open', '');
    }
  }

  #closeLightbox() {
    if (!this.lightbox) return;
    // BUG TROUVE ET CORRIGE (demande explicite du marchand) : fermer le
    // <dialog> natif (.close() ou retrait de [open]) le rend juste
    // invisible - ca ne met JAMAIS en pause une <video> a l'interieur,
    // qui continue de jouer (donc de s'entendre) en arriere-plan tant
    // qu'on ne l'arrete pas explicitement soi-meme. #renderLightboxContent
    // (plus bas) vide bien lightboxMedia, mais seulement au PROCHAIN avis
    // ouvert - jamais a la fermeture. Coupe ici, avant de fermer le dialog :
    // pause() explicite (arrete le son immediatement) puis vidage du
    // conteneur (retire l'element <video> du DOM, libere le fichier) -
    // couvre les 3 facons de fermer (bouton, clic exterieur, Echap),
    // qui passent toutes par cette meme fonction.
    this.lightboxMedia?.querySelectorAll('video').forEach((video) => video.pause());
    if (this.lightboxMedia) this.lightboxMedia.innerHTML = '';
    if (typeof this.lightbox.close === 'function') {
      this.lightbox.close();
    } else {
      this.lightbox.removeAttribute('open');
    }
    this.activeCard?.focus();
    this.activeCard = null;
  }

  #stepImage(direction) {
    if (!this.activeCard) return;
    const images = this.#activeImages();
    if (images.length < 2) return;
    this.activeImageIndex = (this.activeImageIndex + direction + images.length) % images.length;
    this.#renderLightboxContent();
  }

  #activeImages() {
    if (!this.activeCard) return [];
    return Array.from(this.activeCard.querySelectorAll('[data-review-image-src]')).map(
      (el) => el.dataset.reviewImageSrc
    );
  }

  #renderLightboxContent() {
    if (!this.activeCard || !this.lightboxMedia) return;

    const mediaType = this.activeCard.dataset.mediaType;
    this.lightboxMedia.innerHTML = '';

    if (mediaType === 'video') {
      const videoSrc = this.activeCard.querySelector('[data-review-video-src]')?.dataset.reviewVideoSrc;
      if (videoSrc) {
        const video = document.createElement('video');
        video.src = videoSrc;
        video.controls = true;
        video.autoplay = true;
        video.playsInline = true;
        this.lightboxMedia.appendChild(video);
      }
    } else if (mediaType === 'photo') {
      const images = this.#activeImages();
      const src = images[this.activeImageIndex];
      if (src) {
        const img = document.createElement('img');
        img.src = src;
        img.alt = '';
        this.lightboxMedia.appendChild(img);
      }
      const showNav = images.length > 1;
      if (this.lightboxPrev) this.lightboxPrev.hidden = !showNav;
      if (this.lightboxNext) this.lightboxNext.hidden = !showNav;
    }

    if (mediaType !== 'photo') {
      if (this.lightboxPrev) this.lightboxPrev.hidden = true;
      if (this.lightboxNext) this.lightboxNext.hidden = true;
    }

    if (this.lightboxName) {
      this.lightboxName.textContent = this.activeCard.querySelector('.review-card__author')?.textContent || '';
    }
    if (this.lightboxText) {
      this.lightboxText.textContent = this.activeCard.querySelector('.review-card__text')?.textContent || '';
    }

    // 2 - Réponse de la marque : la plupart des avis n'en ont pas, donc
    // l'encart reste caché par défaut (voir son attribut hidden natif dans
    // le markup) - affiché et rempli seulement quand l'avis ouvert a un
    // texte non vide dans son .review-card__reply-text (voir
    // _review-item.liquid).
    if (this.lightboxReply) {
      const replyText = this.activeCard.querySelector('.review-card__reply-text')?.textContent?.trim() || '';
      this.lightboxReply.hidden = replyText === '';
      if (this.lightboxReplyText) this.lightboxReplyText.textContent = replyText;
    }

    const rating = Number(this.activeCard.dataset.reviewRating || 5);
    if (this.lightboxStars) {
      this.lightboxStars.querySelectorAll('[data-star]').forEach((star, index) => {
        star.classList.toggle('review-lightbox__star--filled', index < rating);
      });
    }
  }
}

if (!customElements.get('review-wall')) {
  customElements.define('review-wall', ReviewWall);
}
